import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import {
    BodyTextMedium,
    HeadingText30,
    HeadingText50 as StepTitle,
    Input,
    Checkbox,
    InputType,
    HeadingText10,
    ButtonGroup,
    Button,
    CalloutInline,
    pxToRem,
} from '@modernhealth/vital-components';

import styled from '@emotion/styled';

import { mediaQueries } from 'src/shared/styles/variables';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { navigationService } from 'src/config/rootService';
import { isPhoneNumberFormatValid } from 'src/shared/helpers/phoneValidator';

import { CareNavigationRequest } from '../models/careNavigation';
import { CareNavigationRequestContactPreference } from 'src/generated/webapp_gql';
import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { ButtonGroupTestID, CareNavigationContactInformationStepTestId } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(24),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
    maxWidth: pxToRem(400),
});

const FormContainer = styled.div({
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

const ContactPreferenceContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const InsideFormContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
});

const CheckboxContainer = styled.div({
    marginBottom: pxToRem(24),
    marginTop: pxToRem(16),
    width: '100%',
});

const LinkContainer = styled.div({
    cursor: 'pointer',
});

const CareNavigationCalloutInline = styled.div({
    marginTop: pxToRem(20),
});

export const CareNavigationContactInformationStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    if (isEmpty(location.state)) {
        navigationService.navigate('/care-navigation-request');
    }

    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const contactPhone = location.state.contactPhone || user.phoneNumber || '';
    const [hasValidPhoneNumber, setHasValidPhoneNumber] = useState(
        contactPhone === null || contactPhone?.length === 0
            ? true
            : isPhoneNumberFormatValid(contactPhone),
    );

    const contactPreference =
        location.state.contactPreference !== ''
            ? location.state.contactPreference
            : CareNavigationRequestContactPreference.Phone;

    const [requestState, setRequestState] = useState<CareNavigationRequest>({
        ...location.state,
        contactPhone,
        contactEmail: user.email ?? '',
        contactPreference,
    });

    useEffect(() => {
        setRequestState({
            ...location.state,
            contactPhone: location.state.contactPhone || user.phoneNumber || '',
            contactEmail: user.email ?? '',
            contactPreference,
        });
    }, [contactPreference, location.state, user.phoneNumber, user.email]);

    const isPhone = contactPreference === CareNavigationRequestContactPreference.Phone;
    const [isEditing, setIsEditing] = useState(
        (!contactPhone || !isPhoneNumberFormatValid(contactPhone)) && isPhone,
    );

    let initialFormTitle: string;
    if (isPhone) {
        initialFormTitle = !isEditing
            ? 'careNavigation:care_navigation_contact_information_step.phone_title'
            : 'careNavigation:care_navigation_contact_information_step.new_phone_title';
    } else {
        initialFormTitle = t('careNavigation:care_navigation_contact_information_step.email_title');
    }
    const [formTitle, setFormTitle] = useState(initialFormTitle);

    const inputText = isPhone
        ? t('careNavigation:care_navigation_contact_information_step.phone')
        : t('careNavigation:care_navigation_contact_information_step.email');

    const handleContinue = () => {
        navigationService.navigate('/care-navigation-disclaimer', requestState);
    };

    const handleBack = () => {
        if (requestState.flow === 'normal') {
            navigationService.navigate('/care-navigation-contact-method', requestState);
        } else {
            navigationService.navigate('/care-navigation-goals', requestState);
        }
    };

    const handleEditPhone = () => {
        setFormTitle('careNavigation:care_navigation_contact_information_step.update_phone_title');
        setIsEditing(true);
    };

    const definePhoneError = () => {
        if (!hasValidPhoneNumber) {
            return t('careNavigation:care_navigation_contact_information_step.phone.error');
        }
        return '';
    };

    const handleEditEmail = () => {
        navigationService.navigate('/profile');
    };

    return (
        <Wrapper>
            <CareNavigationSteps
                step={requestState.flow === 'singleClinical' ? 3 : 4}
                flow={requestState.flow}
            />

            <TitleContainer>
                <StepTitle>{`${t(formTitle)}`}</StepTitle>
            </TitleContainer>

            <FormContainer>
                {!isEditing && (
                    <>
                        <InsideFormContainer>
                            <ContactPreferenceContainer>
                                <HeadingText30 marginBottom={4}>{t(inputText)}</HeadingText30>
                                <BodyTextMedium
                                    testID={
                                        CareNavigationContactInformationStepTestId.UserContactPreference
                                    }
                                >
                                    {isPhone
                                        ? requestState.contactPhone
                                        : requestState.contactEmail}
                                </BodyTextMedium>
                            </ContactPreferenceContainer>

                            {isPhone && (
                                <LinkContainer
                                    onClick={handleEditPhone}
                                    data-testid={
                                        CareNavigationContactInformationStepTestId.PhoneEditLink
                                    }
                                >
                                    <HeadingText10 underline fontColor="blue55">
                                        {t('common.edit')}
                                    </HeadingText10>
                                </LinkContainer>
                            )}
                        </InsideFormContainer>
                        {!isPhone && (
                            <CareNavigationCalloutInline>
                                <CalloutInline showIcon={false} variant="info">
                                    <>
                                        {t(
                                            'careNavigation:care_navigation_contact_information_step.email_banner',
                                        )}
                                        <LinkContainer
                                            onClick={handleEditEmail}
                                            data-testid={
                                                CareNavigationContactInformationStepTestId.EmailEditLink
                                            }
                                        >
                                            <HeadingText10 underline fontColor="blue55">
                                                {t(
                                                    'careNavigation:care_navigation_contact_information_step.email_banner_link',
                                                )}
                                            </HeadingText10>
                                        </LinkContainer>
                                    </>
                                </CalloutInline>
                            </CareNavigationCalloutInline>
                        )}
                    </>
                )}

                {isEditing && isPhone && (
                    <>
                        <Input
                            label={t(inputText)}
                            onChange={value => {
                                setHasValidPhoneNumber(isPhoneNumberFormatValid(value.toString()));
                                setRequestState({
                                    ...requestState,
                                    contactPhone: value.toString(),
                                });
                            }}
                            type={InputType.TextField}
                            value={requestState.contactPhone}
                            fieldId="contact-preference"
                            testID={
                                CareNavigationContactInformationStepTestId.ContactPreferenceInput
                            }
                            error={definePhoneError()}
                        />
                        <CheckboxContainer>
                            <Checkbox
                                label={t(
                                    'careNavigation:care_navigation_contact_information_step.save_settings',
                                )}
                                onChange={() => {
                                    setRequestState({
                                        ...requestState,
                                        updatePhone: !requestState.updatePhone,
                                    });
                                }}
                                testId={
                                    CareNavigationContactInformationStepTestId.SaveSettingsCheckbox
                                }
                                value={!!requestState.updatePhone}
                            />
                        </CheckboxContainer>
                    </>
                )}
                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleContinue}
                            disabled={
                                isPhone &&
                                (!hasValidPhoneNumber ||
                                    requestState.contactPhone === null ||
                                    requestState.contactPhone === undefined ||
                                    requestState.contactPhone === '')
                            }
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </FormContainer>
        </Wrapper>
    );
};
